import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "urql";

import { GameForm } from "@/components/forms/GameForm";
import { GameInput, zGameInput } from "@/components/forms/validation/game";
import { buttonVariants } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { graphql } from "@/gen";
import { TrialT } from "@/routes/trial/queries";
import { cn } from "@/utils";

const createGameMutation = graphql(`
    mutation createGame($input: CreateGameInput!) {
        createGame(input: $input) {
            id
            ...useGame_Game
        }
    }
`);

export const GameCreateDialog = () => {
    const [_res, createGame] = useMutation(createGameMutation);
    const { trial } = useOutletContext<{
        trial: TrialT;
    }>();

    const [isOpen, setIsOpen] = useState(false);

    const onSubmit: SubmitHandler<GameInput> = async input => {
        const result = await createGame({
            input: {
                trialId: trial.id,
                player1Color: input.player1Color,
                player2Color: input.player2Color,
                startingPositionFen: input.startingPositionFen,
                startedAt: input.startedAt ? input.startedAt.toISOString() : null,
                scheduledStartAt: input.scheduledStartAt.toISOString(),
                result: input.result,
                finishedAt: input.finishedAt ? input.finishedAt.toISOString() : null,
                heroImageId: input.heroImageId,
                pgnRoundTag: input.pgnRoundTag,
            },
        });

        if (result.error) {
            toast.error("Unable to create game", { description: result.error.message });
            for (const error of result.error.graphQLErrors) {
                // @ts-expect-error typed as string from the API
                form.setError(error.name, { message: error.message });
            }
        } else {
            toast.success("Game was created");
            setIsOpen(false);
        }
    };

    const defaultValues = zGameInput.parse({
        player1Color: "WHITE",
        player2Color: "BLACK",
        startingPositionFen: null,
        startedAt: null,
        scheduledStartAt: trial.round.scheduledStartAt,
        result: null,
        finishedAt: null,
        heroImageId: null,
        pgnRoundTag: null,
    });

    return (
        <Dialog open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
            <DialogTrigger className={cn(buttonVariants())}>
                <PlusIcon />
            </DialogTrigger>
            <DialogContent
                className="max-h-[80vh] w-full max-w-3xl overflow-y-auto"
                aria-describedby={undefined}
            >
                <DialogHeader>
                    <div className="mr-4 flex items-center justify-between">
                        <DialogTitle>Create game</DialogTitle>
                    </div>
                </DialogHeader>
                <GameForm onSubmit={onSubmit} defaultValues={defaultValues} />
            </DialogContent>
        </Dialog>
    );
};
